'use client';

import { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';

/**
 * Service Worker Registration and Update Manager
 *
 * Handles service worker registration, update detection, and provides
 * UI for users to update the app when a new version is available.
 */

export function ServiceWorkerManager() {
  useEffect(() => {
    // The PWA service worker is disabled (see next.config.js) — its
    // fixed-name runtime caches (static-js-assets, static-style-assets,
    // etc.) persist across deploys, and a bad entry written during any
    // earlier broken install kept being served indefinitely, breaking the
    // Work Order annotation editor even after a manual unregister +
    // "Clear site data" in one browser (the SW would just re-register on
    // the next page load, undoing the manual cleanup). Actively
    // unregistering here — rather than just no longer registering — is
    // what makes every already-affected browser self-heal automatically.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => reg.unregister());
      });
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
    }
  }, []);

  return null;
}

/**
 * Service Worker Status Indicator
 * Shows if service worker is active and working
 */
export function ServiceWorkerStatus() {
  const [isActive, setIsActive] = useState(false);
  const [_registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        setRegistration(reg || null);
        setIsActive(!!reg?.active);
      });
    }
  }, []);

  if (!isActive) {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
      <CheckCircle className="h-3 w-3" />
      <span>Offline Ready</span>
    </div>
  );
}

/**
 * Force update service worker
 */
export async function forceUpdateServiceWorker(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
    }
  }
}

/**
 * Unregister service worker (for debugging)
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      return await registration.unregister();
    }
  }
  return false;
}

/**
 * Check if service worker is registered
 */
export async function isServiceWorkerRegistered(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    return !!registration;
  }
  return false;
}

/**
 * Get service worker registration
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if ('serviceWorker' in navigator) {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  }
  return null;
}

/**
 * React hook for service worker state
 */
export function useServiceWorker() {
  const [state, setState] = useState<{
    isRegistered: boolean;
    isActive: boolean;
    updateAvailable: boolean;
    registration: ServiceWorkerRegistration | null;
  }>({
    isRegistered: false,
    isActive: false,
    updateAvailable: false,
    registration: null,
  });

  useEffect(() => {
    async function checkServiceWorker() {
      const registration = await getServiceWorkerRegistration();
      const isActive = !!registration?.active;

      setState({
        isRegistered: !!registration,
        isActive,
        updateAvailable: !!registration?.waiting,
        registration,
      });
    }

    checkServiceWorker();

    // Check every minute
    const interval = setInterval(checkServiceWorker, 60000);
    return () => clearInterval(interval);
  }, []);

  const update = async () => {
    if (state.registration?.waiting) {
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  const forceUpdate = async () => {
    if (state.registration) {
      await state.registration.update();
    }
  };

  return {
    ...state,
    update,
    forceUpdate,
  };
}
